<template>
  <section
    data-cy="messages-view"
    :data-view-ready="!isBusy"
    :class="calculateClassHideMailsFromOtherFolders">
    <div class="floating-wrapper">
      <coozzy-button
        v-if="mobileView"
        class="floating-button"
        design="transparent"
        @click="sendEmailModal">
        <coozzy-edit-icon />
      </coozzy-button>
    </div>
    <div>
      <coozzy-page-title
        :title="$t('message.marketingMessages.messages')"
        :count="messageCount"
        :count-text="$t('message.marketingMessages.messages')"
        :action-text="$t('message.generic.writeEmail')"
        :clear-filter="checkfilter"
        :is-mobile="mobileView"
        :is-floating="mobileView && true"
        @clear-filter="clickOnClear"
        @click="sendEmailModal">
        <template #after-title>
          <div class="col-12 col-md-2 mr-auto">
            <coozzy-button
              v-if="!mobileView"
              design="transparent"
              class="mt-2"
              @click="changeColumnView(!columnView)">
              <coozzy-view-list-icon v-if="columnView" />
              <coozzy-list-icon v-else />
            </coozzy-button>
          </div>
        </template>
      </coozzy-page-title>
      <!-- Filter -->
      <div class="row">
        <div class="col-12 col-md-2 mb-2 mb-md-0 order-0 order-md-0">
          <coozzy-form-input
            ref="searchInputText"
            v-model="querySearch"
            data-cy="messages-search-input"
            :placeholder="$t('message.generic.search')"
            @keyup="searchInputText()" />
        </div>
        <div class="col-4 col-md-2 mb-2 mb-md-0 pr-0 pr-md-2 order-2 order-md-1">
          <coozzy-form-select
            v-model="filterFolder"
            name="numberEntries"
            data-cy="messages-folder-filter-select"
            class="select-entries float-right">
            <option value="FILTER_FOLDER_ALL">
              {{ $t('message.marketingMessages.folderFilter.all') }}
            </option>
            <option value="FILTER_FOLDER_OUTBOUND">
              {{ $t('message.marketingMessages.folderFilter.outbound') }}
            </option>
            <option value="FILTER_FOLDER_INBOUND">
              {{ $t('message.marketingMessages.folderFilter.inbound') }}
            </option>
            <option value="FILTER_FOLDER_SPAM">
              {{ $t('message.marketingMessages.folderFilter.spam') }}
            </option>
            <option value="FILTER_FOLDER_ARCHIVED">
              {{ $t('message.marketingMessages.folderFilter.archive') }}
            </option>
            <option value="FILTER_FOLDER_TRASH">
              {{ $t('message.marketingMessages.folderFilter.trash') }}
            </option>
          </coozzy-form-select>
        </div>
        <div class="col-12 col-md-2 mb-2 order-1 order-md-2">
          <coozzy-multiselect
            v-model="filterMailboxes"
            data-cy="messages-mailbox-filter"
            custom-class="messages-mailbox-filter"
            :options="allMailboxesForCurrentUser"
            :multiple="true"
            :placeholder="$t('message.marketingMessages.mail.mailbox')"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :track-by="'outboundEmail'"
            :label="'outboundEmail'"
            :preselect-first="false"
            :taggable="false">
            <template #option="{ option }">
              <span :class="option.active === false ? 'option-greyed-out' : '' ">
                {{ option.outboundEmail }}
              </span>
            </template>
          </coozzy-multiselect>
        </div>
        <div class="col-6 col-md-2 order-2">
          <coozzy-form-checkbox
            v-model="filterUnreadMsg"
            data-cy="messages-unread-filter"
            :initial="filterUnreadMsg"
            :class="'p-0 w-100 unread-button'"
            name="unreadMessage"
            button>
            {{ $t('message.marketingMessages.unreadMessages') }}
          </coozzy-form-checkbox>
        </div>
        <div
          v-if="mobileView"
          class="col-2 col-md-2 order-3 text-right pl-0">
          <coozzy-button
            class="w-100"
            @click="clickOnClear">
            <coozzy-filter-icon />
          </coozzy-button>
        </div>
      </div>
      <div class="row mt-0 mt-md-2 align-items-center">
        <div class="col-md-2 col-12 mr-sm-0">
          <coozzy-dropdown
            design="green"
            size="sm"
            class="w-100"
            data-cy="messages-bulk-actions-dropdown"
            :text="selectedEntriesText"
            @shown="onBulkDropdownShown"
            @hidden="onBulkDropdownHidden">
            <coozzy-dropdown-item
              v-if="filterFolder !== 'FILTER_FOLDER_TRASH' && filterFolder !== 'FILTER_FOLDER_ARCHIVED'"
              data-cy="messages-bulk-archive-item"
              :disabled="selectedEntriesIds.length === 0"
              @click.stop="onBulkAction('archive')">
              {{ $t('message.generic.archive') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              v-if="filterFolder !== 'FILTER_FOLDER_TRASH'"
              data-cy="messages-bulk-delete-item"
              :disabled="selectedEntriesIds.length === 0"
              @click.stop="onBulkAction('delete')">
              {{ $t('message.generic.delete') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              v-if="filterFolder === 'FILTER_FOLDER_TRASH' && (hasManageMailsRole || isCompanyAdmin)"
              data-cy="messages-bulk-empty-trash-item"
              :disabled="selectedEntriesIds.length === 0"
              @click.stop="onBulkAction('emptyTrash')">
              {{ $t('message.generic.emptyTrash') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              v-if="filterFolder === 'FILTER_FOLDER_TRASH' || filterFolder === 'FILTER_FOLDER_ARCHIVED'"
              data-cy="messages-bulk-restore-item"
              :disabled="selectedEntriesIds.length === 0"
              @click.stop="onBulkAction('restore')">
              {{ $t('message.generic.restore') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-space-divider
              v-if="filterFolder !== 'FILTER_FOLDER_ARCHIVED' && filterFolder !== 'FILTER_FOLDER_TRASH'" />
            <coozzy-dropdown-item
              v-if="filterFolder !== 'FILTER_FOLDER_ARCHIVED' && filterFolder !== 'FILTER_FOLDER_TRASH'"
              data-cy="messages-bulk-mark-read-item"
              :disabled="selectedEntriesIds.length === 0"
              @click.stop="onBulkAction('read')">
              {{ $t('message.marketingMessages.markAsRead') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              v-if="filterFolder !== 'FILTER_FOLDER_ARCHIVED' && filterFolder !== 'FILTER_FOLDER_TRASH'"
              data-cy="messages-bulk-mark-unread-item"
              :disabled="selectedEntriesIds.length === 0"
              @click.stop="onBulkAction('unread')">
              {{ $t('message.marketingMessages.markAsUnRead') }}
            </coozzy-dropdown-item>
          </coozzy-dropdown>
        </div>
        <div class="col-md-2 col-sm-6 col-12 mt-2">
          <template v-if="selectAllVisibleEntries && messageCount > nbrPerPage && messageCount <= 1000">
            <coozzy-form-checkbox
              v-model="selectAllEntries"
              :initial="selectAllEntries"
              :disabled="suppressBulkCheckboxClicks"
              :class="'p-0 w-100 btn-height-auto'"
              name="selectAllEntries"
              button>
              {{ selectAllEntries ? $t('message.generic.unselectHiddenEntries', { amount: messageCount }) : $t('message.generic.selectHiddenEntries', { amount: messageCount }) }}
            </coozzy-form-checkbox>
          </template>
        </div>
        <div
          v-if="!columnView"
          class="col-md-2 col-sm-6 col-12 offset-md-6 mt-2 mt-sm-0">
          <coozzy-form-select
            v-model="nbrPerPage"
            data-cy="messages-page-size-select"
            name="numberEntries"
            class="select-entries float-right">
            <option :value="10">
              10 {{ $t('message.generic.entries') }}
            </option>
            <option :value="50">
              50 {{ $t('message.generic.entries') }}
            </option>
            <option :value="100">
              100 {{ $t('message.generic.entries') }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <div
        :style="shortContent ? 'height:0px;' : '' "
        class="row mt-2"
        :class="columnView ? 'parent-container' : 'simple-container'">
        <div
          :id="columnView ? 'left' : ''"
          :class="columnView ? 'pr-md-0 leftside' : 'col-12 col-md-12'">
          <div @mousemove="onMouseMove($event)">
            <b-table
              ref="messagesTable"
              data-cy="messages-table"
              class="mt-2 messages-table"
              primary-key="id"
              :class="columnView || mobileView ? 'table-column-view' : 'table-row-view'"
              bordered
              hover
              responsive="true"
              selectable
              select-mode="single"
              :busy="isBusy"
              :fields="dataFields"
              :items="messageList"
              :tbody-tr-class="tableRowClassCustom"
              @scrolled="loadMore"
              @row-clicked="onRowClicked"
              @row-dblclicked="onRowDblClicked"
              @row-middle-clicked="onRowMiddleClicked"
              @row-hovered="onRowHovered"
              @row-unhovered="onRowUnHovered">
              <!-- Busy state -->
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <coozzy-spinner />
                </div>
              </template>

              <!-- Headings -->
              <template #head(icons) />
              <template #head(checkbox)>
                <coozzy-form-checkbox
                  :initial="selectAllVisibleEntries"
                  :disabled="disabledCheckbox || suppressBulkCheckboxClicks"
                  @change="(value) => { handleCheckboxesInsideTable(value) }" />
              </template>
              <template #head(date)>
                {{ $t('message.tableColumnHeadings.date') }}
              </template>
              <template #head(from)>
                {{ $t('message.tableColumnHeadings.from') }}
              </template>
              <template #head(to)>
                {{ $t('message.tableColumnHeadings.to') }}
              </template>
              <template #head(subject)>
                {{ $t('message.tableColumnHeadings.subject') }}
              </template>
              <template #head(message)>
                {{ $t('message.tableColumnHeadings.message') }}
              </template>
              <template #head(receivedOpened)>
                {{ $t('message.tableColumnHeadings.receivedOpened') }}
              </template>

              <!-- Data -->
              <template #cell(checkbox)="data">
                <!-- data needs to be used in order to work properly -->
                <coozzy-form-checkbox
                  :id="'check_' + data.item.id"
                  :key="'msg-checkbox-' + data.item.id + '-' + (selectedEntries.includes(data.item.id) ? '1' : '0')"
                  :initial="selectedEntries.includes(data.item.id)"
                  :disabled="disabledCheckbox"
                  @change="(value) => { itemSelected(data.item.id, value) }" />
              </template>
              <template #cell(actions)="data">
                <coozzy-link-icon
                  v-if="isMailHasReferenceIdTicket(data.item)"
                  :id="'tooltip-target-' + data.item.id"
                  class="icon-link-ticket"
                  @click.native="routeToDetail(isMailHasReferenceIdTicket(data.item))"
                  @click.middle="openMiddleDetail(isMailHasReferenceIdTicket(data.item))" />
                <b-tooltip
                  v-if="isMailHasReferenceIdTicket(data.item)"
                  :target="'tooltip-target-' + data.item.id"
                  triggers="hover">
                  {{ isMailHasReferenceIdTicket(data.item).key }} {{ isMailHasReferenceIdTicket(data.item).title }}
                </b-tooltip>
                <template
                  v-if="hasReminder(data.item.id)">
                  <coozzy-reminder-icon
                    v-if="hasReminder(data.item.id) === 'progress'"
                    class="mr-1 ml-1 reminderIcon"
                    @click.native="forwardToReminder(data.item.id)" />
                  <coozzy-check-mark-icon
                    v-else-if="hasReminder(data.item.id) === 'finish'"
                    v-b-tooltip.hover.html="$t('message.marketingMessages.reminderDone', {date: $options.filters.timestampToDate(getReminder(data.item.id)?.done)})"
                    class="mr-1 ml-1" />
                </template>
                <coozzy-reply-icon
                  v-if="data.item.hasReply"
                  class="mr-1 ml-1" />
                <coozzy-forward-icon
                  v-if="data.item.hasForwarding"
                  class="mr-1 ml-1" />
                <coozzy-confidential-icon
                  v-if="data.item.confidential"
                  class="mr-1 ml-1" />
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <p
                  v-else
                  class="mb-0">
                  {{ formatTimestampForList(data.item.timestamp) }}
                </p>
                <div
                  v-if="filterFolder === 'FILTER_FOLDER_OUTBOUND' || (filterFolder === 'FILTER_FOLDER_ALL' && !data.item.inbound)"
                  class="text-center">
                  <template v-if="data.item.mailType === 'MAIL_TYPE_IMIP'">
                    -
                  </template>
                  <template v-else>
                    <template v-if="data.item.deliverData.status === 'DELIVER_STATUS_DELIVERED'">
                      <coozzy-success-icon />
                    </template>
                    <template v-else-if="data.item.deliverData.status === 'DELIVER_STATUS_PENDING'">
                      <coozzy-pending-icon />
                    </template>
                    <template v-else>
                      <coozzy-fail-icon
                        v-b-tooltip.hover.left
                        :title="data.item.deliverData.errorMessage" />
                    </template>
                    /
                    <template v-if="data.item.engagementData.status === 'ENGAGEMENT_STATUS_OPENED'">
                      <coozzy-success-icon />
                    </template>
                    <template v-else>
                      <coozzy-fail-icon />
                    </template>
                  </template>
                </div>
              </template>
              <template #cell(infoMessage)="data">
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <template v-else>
                  <template v-if="data.item.inbound">
                    <template v-if="getMailConnectedWithContacts(data.item).from.contactId">
                      <router-link :to="getContactDetailRoute(getMailConnectedWithContacts(data.item).from.contactId)">
                        {{ getMailConnectedWithContacts(data.item).from.name }}<br>
                      </router-link>
                    </template>
                    <template v-else>
                      <span>{{ data.item.from.name || data.item.from.email }}</span>
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="data.item.to.length && getMailConnectedWithContacts(data.item).to[0].contactId">
                      <router-link :to="getContactDetailRoute(getMailConnectedWithContacts(data.item).to[0].contactId)">
                        {{ getMailConnectedWithContacts(data.item).to[0].name }}<br>
                      </router-link>
                    </template>
                    <template v-else>
                      <span>{{ data.item.to[0]?.name || data.item.to[0]?.email || '-' }}</span>
                    </template>
                  </template>
                </template>
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <p
                  v-else
                  class="fieldToShow mb-0">
                  <coozzy-caret-up-icon
                    v-if="!data.item.inbound"
                    class="d-inline" />
                  <coozzy-caret-down-icon
                    v-else
                    class="d-inline" />
                  {{ decodeHtmlEntitiesIfNeeded(data.item.subject) | displayOptionalValue }}
                  <coozzy-paper-clip-icon v-if="hasVisibleAttachments(data.item)" />
                </p>
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <div
                  v-else
                  v-dompurify-html="getTruncatedHtml(data.item.bodyHtml, messagePreviewMaxLength, data.item.bodyText)"
                  class="text-msg mb-0 fieldToShow" />
              </template>

              <template #cell(icons)="data">
                <coozzy-link-icon
                  v-if="isMailHasReferenceIdTicket(data.item)"
                  :id="'tooltip-target-' + data.item.id"
                  class="icon-link-ticket"
                  @click.native="routeToDetail(isMailHasReferenceIdTicket(data.item))"
                  @click.middle="openMiddleDetail(isMailHasReferenceIdTicket(data.item))" />
                <b-tooltip
                  v-if="isMailHasReferenceIdTicket(data.item)"
                  :target="'tooltip-target-' + data.item.id"
                  triggers="hover">
                  {{ isMailHasReferenceIdTicket(data.item).key }} {{ isMailHasReferenceIdTicket(data.item).title }}
                </b-tooltip>
                <template
                  v-if="hasReminder(data.item.id)">
                  <coozzy-reminder-icon
                    v-if="hasReminder(data.item.id) === 'progress'"
                    class="mr-1 ml-1 reminderIcon"
                    @click.native="forwardToReminder(data.item.id)" />
                  <coozzy-check-mark-icon
                    v-else-if="hasReminder(data.item.id) === 'finish'"
                    v-b-tooltip.hover.html="$t('message.marketingMessages.reminderDone', {date: $options.filters.timestampToDate(getReminder(data.item.id)?.done)})"
                    class="mr-1 ml-1" />
                </template>
                <coozzy-reply-icon
                  v-if="data.item.hasReply"
                  class="mr-1 ml-1" />
                <coozzy-forward-icon
                  v-if="data.item.hasForwarding"
                  class="mr-1 ml-1" />
                <coozzy-confidential-icon
                  v-if="data.item.confidential"
                  class="mr-1 ml-1" />
              </template>
              <template #cell(date)="data">
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <p
                  v-else
                  class="mb-0">
                  {{ formatTimestampForList(data.item.timestamp) }}
                </p>
              </template>

              <template #cell(from)="data">
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <template v-else-if="getMailConnectedWithContacts(data.item).from.contactId">
                  <router-link :to="getContactDetailRoute(getMailConnectedWithContacts(data.item).from.contactId)">
                    {{ getMailConnectedWithContacts(data.item).from.name }}<br>
                  </router-link>
                  <span>
                    {{ data.item.from.email }}
                  </span>
                </template>
                <template v-else>
                  <span>{{ data.item.from.name }}</span><br>
                  <span>{{ data.item.from.email }}</span>
                </template>
              </template>

              <template #cell(to)="data">
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <template v-else-if="data.item.to.length && getMailConnectedWithContacts(data.item).to[0].contactId">
                  <router-link :to="getContactDetailRoute(getMailConnectedWithContacts(data.item).to[0].contactId)">
                    {{ getMailConnectedWithContacts(data.item).to[0].name }}<br>
                  </router-link>
                  <span>
                    {{ data.item.to[0].email }}
                  </span>
                  <span v-if="getRecipientCount(data.item) > 1">+{{ getRecipientCount(data.item) - 1 }}</span>
                </template>
                <template v-else>
                  <template v-if="data.item.to.length === 0 && data.item.cc.length > 0">
                    <template v-if="data.item.cc.length && getMailConnectedWithContacts(data.item).cc[0].contactId">
                      <router-link :to="getContactDetailRoute(getMailConnectedWithContacts(data.item).cc[0].contactId)">
                        {{ getMailConnectedWithContacts(data.item).cc[0].name }}<br>
                      </router-link>
                      <span>
                        {{ data.item.cc[0].email }}
                      </span>
                      <span v-if="getRecipientCount(data.item) > 1">+{{ getRecipientCount(data.item) - 1 }}</span>
                    </template>
                  </template>
                  <template v-else>
                    <span>{{ data.item.to.length > 0 ? getMailConnectedWithContacts(data.item).to[0].name : '-' }}</span><br>
                    <span>{{ data.item.to.length > 0 ? data.item.to[0].email : '-' }} </span>
                    <span v-if="getRecipientCount(data.item) > 1">+{{ getRecipientCount(data.item) - 1 }}</span>
                  </template>
                </template>
              </template>

              <template #cell(subject)="data">
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <p
                  v-else
                  class="fieldToShow">
                  <coozzy-caret-up-icon
                    v-if="!data.item.inbound"
                    class="d-inline" />
                  <coozzy-caret-down-icon
                    v-else
                    class="d-inline" />
                  {{ decodeHtmlEntitiesIfNeeded(data.item.subject) | displayOptionalValue }}
                  <coozzy-paper-clip-icon v-if="hasVisibleAttachments(data.item)" />
                </p>
              </template>

              <template #cell(message)="data">
                <content-loader
                  v-if="getChangingStatusMails.includes(data.item.id)"
                  style="width: 100px;"
                  width="50"
                  height="24">
                  <rect
                    x="0"
                    y="10"
                    rx="3"
                    ry="3"
                    width="50"
                    height="5" />
                </content-loader>
                <div
                  v-else
                  v-dompurify-html="getTruncatedHtml(data.item.bodyHtml, messagePreviewMaxLength, data.item.bodyText)"
                  class="text-msg mb-0 fieldToShow" />
              </template>
              <template #cell(receivedOpened)="data">
                <div
                  v-if="!data.item.inbound"
                  class="text-center">
                  <template v-if="data.item.mailType === 'MAIL_TYPE_IMIP'">
                    -
                  </template>
                  <template v-else>
                    <template v-if="data.item.deliverData.status === 'DELIVER_STATUS_DELIVERED'">
                      <coozzy-success-icon />
                    </template>
                    <template v-else-if="data.item.deliverData.status === 'DELIVER_STATUS_PENDING'">
                      <coozzy-pending-icon />
                    </template>
                    <template v-else>
                      <coozzy-fail-icon
                        v-b-tooltip.hover.left
                        :title="data.item.deliverData.errorMessage" />
                    </template>
                    /
                    <template v-if="data.item.engagementData.status === 'ENGAGEMENT_STATUS_OPENED'">
                      <coozzy-success-icon />
                    </template>
                    <template v-else>
                      <coozzy-fail-icon />
                    </template>
                  </template>
                </div>
                <div
                  v-else
                  class="text-center">
                  -
                </div>
              </template>
            </b-table>
          </div>
        </div>
        <div
          v-if="columnView && !mobileView"
          id="right"
          class="pl-md-0 rightside">
          <coozzy-card
            class="global-card detail-message mb-3 mt-2">
            <message-detail-content
              v-if="selectedMail"
              :key="selectedMail.id + '_' + dynamicId"
              :email="selectedMail"
              :ticket="isMailHasReferenceIdTicket(selectedMail)"
              @restore-mail="mailRestored"
              @reply-mail="replyMail"
              @reply-all-mail="replyAllMail"
              @forward-mail="forwardMail"
              @archive-mail="mailArchived"
              @refresh-list="refreshList"
              @refresh-reminders="refreshReminders"
              @delete-mail="mailDeleted"
              @print-message="printMessage"
              @forward-as-attachment="forwardAsAttachmentLocal" />
            <div
              v-else
              class="block-center">
              <coozzy-envelope-icon class="icon" />  <p class="text-icon">
                {{ $t('message.marketingMessages.selectMessage') }}
              </p>
            </div>
          </coozzy-card>
        </div>
        <div
          v-if="!columnView"
          class="col-12">
          <b-pagination
            v-model="currentPage"
            align="center"
            class="mb-3"
            :total-rows="messageCount"
            :per-page="nbrPerPage"
            @change="paginateTo" />
        </div>
      </div>
    </div>
    <b-modal
      :id="'confirmationModal'"
      ref="confirmationModal"
      data-cy="messages-confirmation-modal"
      :data-view-ready="!disabledDeleteBtn"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingMessages.confirmModal.confirmationTitle')">
      <div
        v-if="disabledDeleteBtn"
        class="text-center">
        <coozzy-spinner />
      </div>
      <div
        v-else
        class="col p-0">
        <p>{{ textConfirmation }}</p>
      </div>
      <div class="col p-0">
        <coozzy-button
          size="small"
          class="mb-0 border"
          design="transparent"
          data-cy="messages-confirm-cancel-button"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          v-if="actionRead === 'read'"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledDeleteBtn"
          @click="setPublish()">
          {{ $t('message.marketingMessages.markAsRead') }}
        </coozzy-button>
        <coozzy-button
          v-else-if="actionRead === 'unread'"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledDeleteBtn"
          @click="setPublish()">
          {{ $t('message.marketingMessages.markAsUnRead') }}
        </coozzy-button>
        <coozzy-button
          v-else-if="actionRead === 'archive'"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledDeleteBtn"
          @click="archiveMails()">
          {{ $t('message.generic.archive') }}
        </coozzy-button>
        <coozzy-button
          v-else-if="actionRead === 'delete'"
          size="small"
          class="float-right mb-0"
          design="green"
          data-cy="messages-confirm-delete-button"
          :disabled="disabledDeleteBtn"
          @click="deleteMails()">
          {{ $t('message.generic.delete') }}
        </coozzy-button>
        <coozzy-button
          v-else-if="actionRead === 'restore'"
          size="small"
          class="float-right mb-0"
          design="green"
          :disabled="disabledDeleteBtn"
          @click="restoreMails()">
          {{ $t('message.generic.restore') }}
        </coozzy-button>
        <coozzy-button
          v-else-if="actionRead === 'emptyTrash'"
          size="small"
          class="float-right mb-0"
          design="green"
          data-cy="messages-confirm-empty-trash-button"
          :disabled="disabledDeleteBtn"
          @click="emptyTrash()">
          {{ $t('message.generic.emptyTrash') }}
        </coozzy-button>
      </div>
    </b-modal>
    <div
      id="eventPopover"
      ref="eventPopover"
      class="eventPopover"
      style="display: none; left: 0px; top: 0px;">
      <div class="row">
        <div class="employees-calendar shadow py-3 px-4">
          <div class="row">
            <div class="col-12 mb-1">
              <p><b ref="popoverSubject" /></p>
              <shadow-html ref="popoverBodyShadow" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <reminder-creation-modal
      v-if="reminderForEdit !== null"
      ref="reminder-creation"
      :key="reminderForEdit ? 'modal-creation'+reminderForEdit.id : 'modal-creation'"
      :reminder-detail="reminderForEdit"
      :source="'message'"
      :references-ids="[selectedMail.id || selectedMail.objectID]"
      @update-reminder="refreshReminders()" />
    <reminder-details-modal
      v-if="selectedReminder !== null"
      ref="detail-reminder-modal"
      :key="selectedReminder.id"
      :reminder-detail="selectedReminder"
      @create-reminder-click="createReminderClicked"
      @update-reminder="refreshReminders()" />
  </section>
</template>

<script>
import Vue from 'vue'
import MessageApi from '../misc/apis/MessageApi'
import { executeBulkInChunks } from '../misc/utils/bulkOperations'
import ContactApi from '../misc/apis/ContactApi'
import TicketApi from '../misc/apis/TicketApi'
import CoozzyFormCheckbox from '../framework/components/form/checkbox/CoozzyFormCheckbox'
import CoozzyButton from '../framework/components/button/CoozzyButton'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CoozzyMultiselect from '@/framework/components/multiselect/CoozzyMultiselect'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import CoozzyCaretUpIcon from '@/framework/components/icons/CoozzyCaretUpIcon'
import CoozzyCaretDownIcon from '@/framework/components/icons/CoozzyCaretDownIcon'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle'
import CoozzyDropdownSpaceDivider from '@/framework/components/dropdown/CoozzyDropdownSpaceDivider'
import CoozzyPaperClipIcon from '../framework/components/icons/CoozzyPaperClipIcon'
import { user } from '@/mixins/user'
import { contact } from '@/mixins/contact'
import { eventBus, mail } from '@/mixins/mail'
import { tables } from '@/mixins/tables'
import { responsivity } from '@/mixins/responsivity'
import { ContentLoader } from 'vue-content-loader'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput'
import CoozzyReplyIcon from '@/framework/components/icons/CoozzyReplyIcon'
import CoozzyForwardIcon from '@/framework/components/icons/CoozzyForwardIcon'
import CoozzyCheckMarkIcon from '@/framework/components/icons/CoozzyCheckMarkIcon'
import CoozzyConfidentialIcon from '@/framework/components/icons/CoozzyConfidentialIcon'
import ReminderApi from '@/misc/apis/ReminderApi'
import ReminderDetailsModal from '@/calendar/components/ReminderDetailsModal'
import ReminderCreationModal from '@/calendar/components/ReminderCreationModal'
import CoozzyLinkIcon from '@/framework/components/icons/CoozzyLinkIcon.vue'
import CoozzyReminderIcon from '@/framework/components/icons/CoozzyReminderIcon.vue'
import CoozzyViewListIcon from '@/framework/components/icons/CoozzyViewListIcon.vue'
import MessageDetailContent from '@/contact/components/MessageDetailContent.vue'
import CoozzySuccessIcon from '@/framework/components/icons/CoozzySuccessIcon.vue'
import CoozzyFailIcon from '@/framework/components/icons/CoozzyFailIcon.vue'
import CoozzyPendingIcon from '@/framework/components/icons/CoozzyPendingIcon.vue'
import Split from 'split.js'
import ShadowHtml from '@/framework/components/ShadowHtml.vue'
import CoozzyListIcon from '@/framework/components/icons/CoozzyListIcon.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyEnvelopeIcon from '@/framework/components/icons/CoozzyEnvelopeIcon.vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzyFilterIcon from '@/framework/components/icons/CoozzyFilterIcon.vue'

export default {
  name: 'MessagesView',
  components: {
    CoozzyFilterIcon,
    CoozzyEditIcon,
    CoozzyEnvelopeIcon,
    CoozzyCard,
    CoozzyListIcon,
    CoozzyPendingIcon,
    CoozzyFailIcon,
    CoozzySuccessIcon,
    CoozzyReminderIcon,
    CoozzyLinkIcon,
    CoozzyForwardIcon,
    CoozzyReplyIcon,
    CoozzyFormInput,
    CoozzyDropdownSpaceDivider,
    CoozzyPageTitle,
    CoozzySpinner,
    CoozzyDropdownItem,
    CoozzyCaretDownIcon,
    CoozzyCaretUpIcon,
    CoozzyFormSelect,
    CoozzyDropdown,
    CoozzyMultiselect,
    CoozzyButton,
    CoozzyFormCheckbox,
    CoozzyPaperClipIcon,
    ContentLoader,
    CoozzyCheckMarkIcon,
    ReminderDetailsModal,
    ReminderCreationModal,
    CoozzyConfidentialIcon,
    CoozzyViewListIcon,
    MessageDetailContent,
    ShadowHtml
  },
  mixins: [user, contact, mail, tables, responsivity],
  props: {
    s: {
      type: String,
      default: null
    },
    f: {
      type: String,
      default: null
    },
    m: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedEmailId: null,
      abortController: null,
      inFlightLoadMorePage: null,
      shortContent: false,
      isBusy: false,
      querySearch: '',
      checkfilter: false,
      filterFolder: 'FILTER_FOLDER_INBOUND',
      filterMailboxes: [],
      disabledDeleteBtn: false,
      actionRead: null,
      emailToReply: null,
      textConfirmation: null,
      selectedMail: null,
      selectedEntries: [],
      messageList: [],
      listTickets: [],
      usedContacts: [],
      nbrPerPage: 10,
      currentPage: 1,
      messageCount: 0,
      appRefreshAction: '',
      fields: [
        {
          key: 'checkbox',
          label: '',
          tdClass: 'align-middle checkbox-cell checkbox-td',
          view: null
        },
        {
          key: 'infoMessage',
          label: '',
          tdClass: 'align-middle width-full-content',
          thClass: 'align-middle',
          view: true
        },
        {
          key: 'actions',
          label: '',
          tdClass: 'align-middle text-center adjustWidth',
          thClass: 'align-middle text-center adjustWidth',
          view: true
        },
        {
          key: 'icons',
          label: '',
          tdClass: 'align-middle text-center adjustWidth',
          thClass: 'align-middle text-center adjustWidth',
          view: false
        },
        {
          key: 'date',
          label: this.$t('message.tableColumnHeadings.date'),
          tdClass: 'align-middle text-left',
          thClass: 'align-middle text-left',
          view: false
        },
        {
          key: 'from',
          label: this.$t('message.tableColumnHeadings.from'),
          tdClass: 'align-middle',
          thClass: 'align-middle',
          view: false
        },
        {
          key: 'to',
          label: this.$t('message.tableColumnHeadings.to'),
          tdClass: 'align-middle',
          thClass: 'align-middle',
          view: false
        },
        {
          key: 'subject',
          label: this.$t('message.tableColumnHeadings.subject'),
          thClass: 'align-middle',
          tdClass: 'fieldToShow',
          view: false
        },
        {
          key: 'message',
          label: this.$t('message.tableColumnHeadings.message'),
          thClass: 'align-middle message-text',
          tdClass: 'fieldToShow',
          view: false
        }
      ],
      fieldForOutbound:
      {
        key: 'receivedOpened',
        label: this.$t('message.tableColumnHeadings.receivedOpened'),
        thClass: 'align-middle',
        tdClass: 'align-middle',
        view: false
      },
      disabledCheckbox: false,
      mailIdDeletedForClosingModal: [],
      filterUnreadMsg: false,
      reminders: [],
      selectedReminder: null,
      reminderForEdit: null,
      dynamicId: '',
      debounceTimeout: null,
      selectAllVisibleEntries: false,
      selectAllEntries: false,
      allEntries: [],
      dataFields: [],
      columnView: true,
      mailBoxFilterChanged: false,
      lastSearchedQuery: '',
      splitInstance: null,
      messagePreviewMaxLength: 70,
      messagePreviewUpdateTimeout: null,
      messagePreviewUpdateLastRun: 0,
      pendingListRestore: null,
      maxLoadedPageInColumnView: 1,
      suppressBulkCheckboxClicks: false,
      bulkDropdownHiddenTimeout: null
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.navigation.messages')
    }
  },
  computed: {
    ...mapGetters('message', ['getSentMails', 'getMailboxesForUserId', 'getChangingStatusMails', 'getNewEmailsArrived']),
    selectedEntriesText() {
      const count = this.selectAllEntries && this.selectAllVisibleEntries ? this.messageCount : this.selectedEntries.length
      return this.$t('message.generic.selectedEntries') + ':  ' + count
    },
    selectedEntriesIds() {
      return this.selectAllEntries ? this.allEntries : this.selectedEntries
    },
    calculateClassHideMailsFromOtherFolders() {
      if (this.filterFolder === 'FILTER_FOLDER_INBOUND' || this.filterFolder === 'FILTER_FOLDER_OUTBOUND' || this.filterFolder === 'FILTER_FOLDER_ALL') {
        return 'other'
      }
      if (this.filterFolder === 'FILTER_FOLDER_ARCHIVED') {
        return 'archive'
      }
      if (this.filterFolder === 'FILTER_FOLDER_TRASH') {
        return 'trash'
      }
      return ''
    }
  },
  watch: {
    getSentMails: async function (newVal) {
      if (newVal.length > 0) {
        this.loadMailboxesForUserId(this.userId)
        if (this.allMailboxesForCurrentUser.length > 0) {
          if (this.columnView) {
            await this.refreshColumnViewAfterSend()
          } else {
            this.captureListRestoreState()
            this.filterMessages(this.currentPage)
          }
        }
      }
    },
    checkfilter: function () {
      this.clearCheckbox()
    },
    filterUnreadMsg() {
      this.currentPage = 1
      this.filterMessages(1)
      this.checkfilter = true
    },
    allMailboxesForCurrentUser: function (newVal, oldVal) {
      // Restore mailboxes filter with only that mailboxes that are actually allowed by the user
      if (this.m && this.allMailboxesForCurrentUser && this.allMailboxesForCurrentUser.length) {
        const mailboxIds = this.m.split(',')
        this.filterMailboxes = this.allMailboxesForCurrentUser.filter(box => mailboxIds.includes(box.id))
      }
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        // Skip the redundant wipe-and-reload while a post-send list restore is in flight
        // (COOZ-15299). loadMailboxesForUserId can refresh this collection right after a
        // reply/forward send, which otherwise undoes the scroll/selection restore.
        if (this.pendingListRestore) {
          return
        }
        this.checkfilter = true
        this.currentPage = 1
        this.clearCheckbox()
        this.filterMessages(1)
      }
    },
    filterFolder: function (newVal, oldVal) {
      this.checkfilter = true
      this.currentPage = 1
      this.clearCheckbox()
      this.filterMessages(1)
      if (newVal !== oldVal && this.$route.query.f !== newVal) {
        this.$router.replace({
          name: this.$route.name,
          query: {
            s: this.s,
            f: newVal,
            m: this.m
          }
        })
      }
      // Fix: Only add/remove receivedOpened field if not already present/absent
      if (newVal !== 'FILTER_FOLDER_OUTBOUND' && newVal !== 'FILTER_FOLDER_ALL') {
        this.fields = this.fields.filter(x => x.key !== 'receivedOpened')
      } else {
        if (!this.fields.find(x => x.key === 'receivedOpened')) {
          this.fields.push(this.fieldForOutbound)
        }
      }
      this.dataFields = this.fields.filter(x => x.view === this.columnView || x.view === null)
    },
    querySearch: function (newVal, oldVal) {
      if (newVal.trim() !== oldVal.trim()) {
        this.checkfilter = true
        this.currentPage = 1
        this.clearCheckbox()
        if (this.$route.query.s !== newVal) {
          this.$router.replace({
            name: this.$route.name,
            query: {
              s: newVal,
              f: this.f,
              m: this.m
            }
          })
        }
      }
    },
    filterMailboxes: function (newVal, oldVal) {
      this.mailBoxFilterChanged = true
      this.checkfilter = true
      this.clearCheckbox()
      if (!this.columnView) {
        this.currentPage = 1
        this.handleCheckboxesInsideTable(false)
      }
      this.filterMessages(1)

      const newString = newVal.map(e => e.id).join(',')
      const oldString = oldVal.map(e => e.id).join(',')

      localStorage.setItem('filterMailboxesMessagesOverview', newString)

      this.loadEmailCounts({
        mailboxIds: newString !== '' ? newString : this.allMailboxesForCurrentUser.map(box => box.id).join(),
        force: true
      })
      if (newString !== oldString && this.$route.query.m !== newString) {
        this.$router.replace({
          name: this.$route.name,
          query: {
            s: this.s,
            f: this.f,
            m: newString
          }
        })
      }
    },
    currentPage: function () {
      if (!this.columnView) {
        this.handleCheckboxesInsideTable(false)
      }
    },
    nbrPerPage: function (value) {
      this.filterMessages(1)
      this.checkfilter = true
      this.handleCheckboxesInsideTable(false)
      if (!this.columnView) {
        localStorage.setItem('nbrPerPageMessagesOverview', value)
      }
    },
    selectAllEntries: async function (newValue) {
      if (this.suppressBulkCheckboxClicks) {
        if (newValue) {
          this.$nextTick(() => {
            this.selectAllEntries = false
          })
        }
        return
      }
      if (newValue) {
        this.handleCheckboxesInsideTable(newValue)
        this.allEntries = await this.filterAllMessages()
      }
    },
    getNewEmailsArrived (arrived) {
      if (arrived && !this.mailBoxFilterChanged) {
        this.filterMessages(1, true)
      }
    }
  },
  beforeDestroy() {
    const tableBody = this.$refs.messagesTable.$el.querySelector('.b-table tbody')
    tableBody.removeEventListener('scroll', this.handleScroll)
    eventBus.$off('archive-mail')
    eventBus.$off('restore-mail')
    eventBus.$off('deleteMail')
    eventBus.$off('refresh-list')
    // Clean up Split.js instance
    if (this.splitInstance) {
      this.splitInstance.destroy()
      this.splitInstance = null
    }
    if (this.popoverRafId) {
      cancelAnimationFrame(this.popoverRafId)
      this.popoverRafId = null
    }
    window.removeEventListener('resize', this.handleWindowResize)
    if (this.messagePreviewUpdateTimeout) {
      clearTimeout(this.messagePreviewUpdateTimeout)
      this.messagePreviewUpdateTimeout = null
    }
    if (this.bulkDropdownHiddenTimeout) {
      clearTimeout(this.bulkDropdownHiddenTimeout)
      this.bulkDropdownHiddenTimeout = null
    }
  },
  mounted: function () {
    // Only initialize Split.js if columnView is true
    if (localStorage.getItem('is-column-view-message')) {
      this.columnView = localStorage.getItem('is-column-view-message') === 'true'
      if (this.columnView) {
        this.nbrPerPage = 20
      } else {
        this.nbrPerPage = this.getNormalizedMessagesPerPage()
      }
    } else {
      this.columnView = true
      this.nbrPerPage = 20
    }
    // Initialize Split.js only if columnView is true
    if (this.columnView) {
      this.$nextTick(() => {
        this.initializeSplit()
      })
    }
    window.addEventListener('resize', this.handleWindowResize)
    this.$nextTick(() => {
      this.updateMessagePreviewMaxLength()
    })
    this.resetStatusMails()
    this.$nextTick(async function () {
      this.isBusy = true
      try {
        await this.loadMailboxesForUserId(this.userId)
      } catch (e) {
        console.error(e)
      }
      if (this.allMailboxesForCurrentUser.length > 0) {
        this.filterMessages(1)
      } else {
        this.isBusy = false
      }
      if (this.$refs.searchInputText && !this.mobileView) {
        this.$refs.searchInputText?.setFocusInput()
      }
    })
    // this.countUnreadMails(true)
    // Restore folder filter
    if (this.f && ['FILTER_FOLDER_INBOUND', 'FILTER_FOLDER_OUTBOUND', 'FILTER_FOLDER_ALL', 'FILTER_FOLDER_ARCHIVED', 'FILTER_FOLDER_TRASH', 'FILTER_FOLDER_SPAM'].includes(this.f)) {
      this.filterFolder = this.f
    }
    // Restore mailboxes filter with only that mailboxes that are actually allowed by the user
    if (this.m && this.allMailboxesForCurrentUser && this.allMailboxesForCurrentUser.length) {
      const mailboxIds = this.m.split(',')
      this.filterMailboxes = this.allMailboxesForCurrentUser.filter(box => mailboxIds.includes(box.id))
    }
    if (localStorage.getItem('filterMailboxesMessagesOverview') && localStorage.getItem('filterMailboxesMessagesOverview') !== '' && this.allMailboxesForCurrentUser && this.allMailboxesForCurrentUser.length) {
      const mailboxIds = localStorage.getItem('filterMailboxesMessagesOverview')?.split(',') || []
      this.filterMailboxes = this.allMailboxesForCurrentUser.filter(box => mailboxIds.includes(box.id))
    }
    if (this.s && this.s.length > 0) {
      this.querySearch = this.s
    }
    if (this.filterFolder !== 'FILTER_FOLDER_OUTBOUND' && this.filterFolder !== 'FILTER_FOLDER_ALL') {
      this.fields = this.fields.filter(x => x.key !== 'receivedOpened')
    } else {
      if (!this.fields.find(x => x.key === 'receivedOpened')) {
        this.fields.push(this.fieldForOutbound)
      }
    }
    this.dataFields = this.fields.filter(x => x.view === this.columnView || x.view === null)
    this.$nextTick(() => {
      if (this.$refs.messagesTable) {
        const tableBody = this.$refs.messagesTable.$el.querySelector('.b-table tbody')
        tableBody.addEventListener('scroll', this.handleScroll)
      }
    })
    eventBus.$on('archive-mail', (event) => {
      this.mailArchived(event)
    })
    eventBus.$on('restore-mail', (event) => {
      this.mailRestored(event)
    })
    eventBus.$on('deleteMail', (event) => {
      this.mailDeleted(event)
    })
    eventBus.$on('refresh-list', (event) => {
      this.refreshList(event)
    })
    if (this.mobileView) {
      this.columnView = true
      this.nbrPerPage = 20
      this.dataFields = this.fields.filter(x => x.view === this.columnView || x.view === null)
      this.$nextTick(() => {
        this.initializeSplit()
      })
    }
  },
  methods: {
    ...mapMutations('message', [
      'setSendMailContacts',
      'setEmailToForwardAsAttachment',
      'setSendMailPreselectedContact',
      'setSendMailReferenceIds',
      'setSendMailEmailToReply',
      'setSendMailCC',
      'setSendMailOnlyCurrentContact',
      'setSendMailHasContext',
      'setMailsLoadedAt',
      'addReadMail',
      'addUnreadMail',
      'setChangingStatusMails',
      'resetStatusMails',
      'setSubject'
    ]),
    getNormalizedMessagesPerPage() {
      const storageKey = 'nbrPerPageMessagesOverview'
      const rawValue = localStorage.getItem(storageKey)
      const allowedValues = [10, 50, 100]
      const fallbackValue = 10

      const parsedValue = rawValue ? parseInt(rawValue, 10) : fallbackValue
      const migratedValue = [500, 1000].includes(parsedValue) ? 100 : parsedValue
      const normalizedValue = allowedValues.includes(migratedValue) ? migratedValue : fallbackValue

      localStorage.setItem(storageKey, normalizedValue)
      return normalizedValue
    },
    getTruncatedHtml(html, maxLength = 70, fallbackText = '') {
      if (!html) {
        if (fallbackText) {
          const text = fallbackText.replace(/\s+/g, ' ').trim()
          if (text.length <= maxLength) {
            return text
          }
          return text.substring(0, maxLength).trim() + '...'
        }
        return '-'
      }
      // First sanitize the HTML
      let sanitized = this.sanitizeHtml(html)
      // Remove images with cid: sources (they can't be loaded in browser previews)
      sanitized = sanitized.replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi, '')
      // Extract plain text to calculate truncation point
      let plainText = this.convertToPlainWithBreakingLines(sanitized)
      // Remove unnecessary spaces: multiple spaces become single space, trim edges
      plainText = plainText.replace(/\s+/g, ' ').trim()
      // Always return plain text (not HTML) to avoid rendering tables/complex HTML in the list
      if (plainText.length <= maxLength) {
        return plainText
      }
      // Truncate based on text content
      const truncatedText = plainText.substring(0, maxLength).trim() + '...'
      return truncatedText
    },
    tableRowClassCustom(item, type) {
      let unreadStatus = this.tableRowClass(item, type)
      if (item && this.selectedEmailId === item.id) {
        unreadStatus = unreadStatus + ' b-table-row-selected'
      }
      return unreadStatus
    },
    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target
      if (scrollHeight - parseInt(scrollTop) - clientHeight < 20) {
        this.loadMore()
      }
    },
    captureListRestoreState() {
      const state = {
        selectedEmailId: this.selectedEmailId,
        selectedEntries: Array.isArray(this.selectedEntries) ? [...this.selectedEntries] : [],
        windowScrollY: window.scrollY,
        tableScrollTop: null,
        selectedRowOffsetWithinViewport: null
      }

      if (this.columnView && this.$refs.messagesTable?.$el) {
        const tableBody = this.$refs.messagesTable.$el.querySelector('.b-table tbody')
        if (tableBody) {
          state.tableScrollTop = tableBody.scrollTop
          if (state.selectedEmailId) {
            // Capture the on-screen offset of the selected row inside the tbody viewport.
            // Restoring this offset (instead of an absolute scrollTop) keeps the user's
            // visual context stable even if surrounding row heights shift after refresh.
            const selectedRow = tableBody.querySelector('tr.b-table-row-selected')
            if (selectedRow) {
              const tbodyRect = tableBody.getBoundingClientRect()
              const rowRect = selectedRow.getBoundingClientRect()
              state.selectedRowOffsetWithinViewport = rowRect.top - tbodyRect.top
            }
          }
        }
      }

      this.pendingListRestore = state
    },
    applyPendingSelectionImmediate() {
      // Synchronously re-apply the selected mail right after messageList is refreshed,
      // so <message-detail-content> never sees selectedMail === null and therefore
      // never unmounts/flickers during the post-send refresh.
      if (!this.pendingListRestore) {
        return
      }
      const restoreState = this.pendingListRestore
      if (restoreState.selectedEmailId) {
        const refreshedSelection = this.messageList.find(mail => mail.id === restoreState.selectedEmailId)
        if (refreshedSelection) {
          this.selectedEmailId = refreshedSelection.id
          this.selectedMail = refreshedSelection
        } else {
          // Selected mail no longer in list (e.g. moved/archived as side effect): clear cleanly.
          this.selectedEmailId = null
          this.selectedMail = null
        }
      }
      if (Array.isArray(restoreState.selectedEntries) && restoreState.selectedEntries.length) {
        const remainingIds = new Set(this.messageList.map(mail => mail.id))
        this.selectedEntries = restoreState.selectedEntries.filter(id => remainingIds.has(id))
      }
    },
    restoreScrollAfterRefresh() {
      if (!this.pendingListRestore) {
        return
      }
      const restoreState = this.pendingListRestore
      const applyScroll = () => {
        if (!this.$refs.messagesTable?.$el) {
          return
        }
        if (this.columnView) {
          const tableBody = this.$refs.messagesTable.$el.querySelector('.b-table tbody')
          if (!tableBody) {
            return
          }
          let appliedAnchoredScroll = false
          if (restoreState.selectedEmailId && Number.isFinite(restoreState.selectedRowOffsetWithinViewport)) {
            const selectedRow = tableBody.querySelector('tr.b-table-row-selected')
            if (selectedRow) {
              // Anchor on the selected row: shift the tbody scroll so the selected row
              // sits at the same on-screen offset within the tbody viewport as before
              // the refresh. We use bounding rects (not offsetTop) because the row's
              // offsetParent is the document body, not the tbody, in this layout.
              const tbodyTop = tableBody.getBoundingClientRect().top
              const currentRowOffsetWithinViewport = selectedRow.getBoundingClientRect().top - tbodyTop
              const delta = currentRowOffsetWithinViewport - restoreState.selectedRowOffsetWithinViewport
              tableBody.scrollTop = Math.max(0, tableBody.scrollTop + delta)
              appliedAnchoredScroll = true
            }
          }
          if (!appliedAnchoredScroll && Number.isFinite(restoreState.tableScrollTop)) {
            tableBody.scrollTop = restoreState.tableScrollTop
          }
        } else if (Number.isFinite(restoreState.windowScrollY)) {
          window.scrollTo(0, restoreState.windowScrollY)
        }
      }
      // First pass: right after Vue patches the DOM with the new list.
      this.$nextTick(() => {
        applyScroll()
        // Second pass on the next animation frame to compensate for late layout shifts
        // caused by async work kicked off in filterMessages (loadContacts, getTickets,
        // image/contact resolution). Without this, scrollTop can drift by a few rows.
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            applyScroll()
            this.pendingListRestore = null
          })
        } else {
          this.pendingListRestore = null
        }
      })
    },
    changeColumnView(newVal) {
      // Clean up existing Split instance if switching away from column view
      if (!newVal && this.splitInstance) {
        this.splitInstance.destroy()
        this.splitInstance = null
      }

      this.columnView = newVal
      localStorage.setItem('is-column-view-message', newVal)
      this.dataFields = this.fields.filter(x => x.view === this.columnView || x.view === null)
      if (newVal) {
        this.nbrPerPage = 20
        // Initialize Split.js when switching to column view
        this.$nextTick(() => {
          this.initializeSplit()
          this.updateMessagePreviewMaxLength()
        })
      } else {
        this.nbrPerPage = this.getNormalizedMessagesPerPage()
        this.$nextTick(() => {
          this.updateMessagePreviewMaxLength()
        })
      }
    },
    handleWindowResize() {
      this.updateMessagePreviewMaxLength()
    },
    updateMessagePreviewMaxLength() {
      if (!this.columnView) {
        if (this.messagePreviewUpdateTimeout) {
          clearTimeout(this.messagePreviewUpdateTimeout)
          this.messagePreviewUpdateTimeout = null
        }
        this.messagePreviewMaxLength = 70
        return
      }

      const throttleDelayMs = 5
      const now = Date.now()
      const elapsed = now - this.messagePreviewUpdateLastRun

      if (elapsed >= throttleDelayMs) {
        if (this.messagePreviewUpdateTimeout) {
          clearTimeout(this.messagePreviewUpdateTimeout)
          this.messagePreviewUpdateTimeout = null
        }
        this.messagePreviewUpdateLastRun = now
        this.applyMessagePreviewMaxLength()
        return
      }

      if (this.messagePreviewUpdateTimeout) {
        clearTimeout(this.messagePreviewUpdateTimeout)
      }
      this.messagePreviewUpdateTimeout = setTimeout(() => {
        this.messagePreviewUpdateTimeout = null
        this.messagePreviewUpdateLastRun = Date.now()
        this.applyMessagePreviewMaxLength()
      }, throttleDelayMs - elapsed)
    },
    applyMessagePreviewMaxLength() {
      if (!this.columnView) {
        this.messagePreviewMaxLength = 70
        return
      }

      this.$nextTick(() => {
        let containerWidth = 0
        const leftPanel = document.getElementById('left')
        if (this.columnView && leftPanel) {
          containerWidth = leftPanel.clientWidth
        } else if (this.$refs.messagesTable && this.$refs.messagesTable.$el) {
          containerWidth = this.$refs.messagesTable.$el.clientWidth
        }

        if (!containerWidth) {
          return
        }

        // Keep subject/actions space reserved and scale body preview with available room.
        const reservedWidth = this.columnView ? 200 : 100
        const approxCharsPerLine = 7
        const availableMessageWidth = Math.max(140, containerWidth - reservedWidth)
        const dynamicLength = Math.floor(availableMessageWidth / approxCharsPerLine)
        this.messagePreviewMaxLength = Math.max(25, Math.min(dynamicLength, 280))
      })
    },
    initializeSplit() {
      // Only initialize if both elements exist and Split isn't already initialized
      if (document.getElementById('left') && document.getElementById('right') && !this.splitInstance) {
        const sizes = localStorage.getItem('splitSizesMessagesView')
          ? JSON.parse(localStorage.getItem('splitSizesMessagesView'))
          : [30, 70]
        this.splitInstance = Split(['#left', '#right'], {
          sizes: sizes,
          minSize: 100,
          gutterSize: 5,
          cursor: 'col-resize',
          onDrag: () => {
            this.updateMessagePreviewMaxLength()
          },
          onDragEnd: sizes => {
            localStorage.setItem('splitSizesMessagesView', JSON.stringify(sizes))
            this.updateMessagePreviewMaxLength()
          }
        })
      }
    },
    ...mapActions('message', ['loadMailboxesForUserId']),
    getReminders() {
      const msgIds = this.messageList.map(mail => mail.id)
      if (msgIds.length > 0) {
        ReminderApi.getRemindersByReferenceIds2(msgIds, this.$store.getters['user/getAccountId'], true)
          .then(response => {
            this.reminders = response.reminders.filter(x => x.referenceIds.find(y => y && y.includes('mail_')))
          })
      }
    },
    getTickets() {
      const ticketReferenceIds = this.messageList.flatMap(mail => mail.referenceIds).filter(x => x && x.includes('ticket_'))
      if (ticketReferenceIds.length > 0) {
        TicketApi.getTicketByIds(ticketReferenceIds)
          .then(response => {
            this.listTickets = response.tickets
          })
      }
    },
    isMailHasReferenceIdTicket(message) {
      return this.listTickets?.find(ticket => message.referenceIds.includes(ticket.id))
    },
    refreshReminders() {
      this.getReminders()
      this.reminderForEdit = null
      this.$bvModal?.hide('modal-detail-mail')
    },
    createReminderClicked(reminder = null) {
      if (reminder !== null) {
        this.reminderForEdit = reminder
      } else {
        this.reminderForEdit = null
      }
      this.$nextTick(function () {
        this.$refs['reminder-creation']?.show()
      })
    },
    hasReminder(id) {
      let item = this.reminders.filter(x => x.referenceIds.find(y => y && y.includes(id)))
      if (item.length > 0) {
        item = item.sort((a, b) => (a.date < b.date ? 1 : -1))
        if (item[0].done === '0') {
          return 'progress'
        } else {
          return 'finish'
        }
      }
      return null
    },
    getReminder(id) {
      let item = this.reminders.filter(x => x.referenceIds.find(y => y && y.includes(id)))
      item = item.sort((a, b) => (a.date < b.date ? 1 : -1))
      if (item.length > 0) {
        return item[0]
      }
      return null
    },
    forwardToReminder(id) {
      this.selectedReminder = this.getReminder(id)
      this.$nextTick(function () {
        this.$refs['detail-reminder-modal']?.show()
      })
    },
    searchInputText() {
      if (this.lastSearchedQuery.trim() !== this.querySearch.trim()) {
        this.lastSearchedQuery = this.querySearch
        this.checkfilter = true
        this.clearCheckbox()
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout)
        }
        this.debounceTimeout = setTimeout(() => {
          this.debounceTimeout = null
          this.filterMessages(1)
        }, 300)
      }
    },
    getMessageText(bodyText) {
      if (bodyText) {
        return this.getTooltipCorrectString(bodyText).trim()
      }
    },
    hasVisibleAttachments(mail) {
      if (!mail?.attachmentMediaIds?.length) {
        return false
      }
      const cidMappedMediaIds = new Set((mail.cidMappings || []).map(mapping => mapping.mediaId).filter(Boolean))
      return mail.attachmentMediaIds.some(mediaId => !cidMappedMediaIds.has(mediaId))
    },
    clearCheckbox() {
      this.selectedEntries = []
      this.selectedMail = null
      this.selectAllVisibleEntries = false
      this.selectAllEntries = false
    },
    sendEmailModal() {
      this.setSendMailHasContext(true)
      this.setSendMailContacts([])
      this.$nextTick(function () {
        if (this.getMailboxesForUserId(this.userId).length === 0) {
          this.$nextTick(function () {
            Vue.toasted.show(this.$t('message.settings.mailbox.configMailboxToast'), { type: 'error' })
          })
        } else {
          this.callShowSendMailModal()
        }
      })
    },
    forwardAsAttachmentLocal(mediaList, email) {
      this.setSubject('Fwd: ' + email.subject)
      this.setSendMailHasContext(true)
      this.setSendMailContacts([])
      this.setEmailToForwardAsAttachment(mediaList)
      this.$nextTick(function () {
        if (this.getMailboxesForUserId(this.userId).length === 0) {
          this.$nextTick(function () {
            Vue.toasted.show(this.$t('message.settings.mailbox.configMailboxToast'), { type: 'error' })
          })
        } else {
          this.callShowSendMailModal()
        }
      })
    },
    changeStatus(type) {
      if (type === 'read') {
        this.actionRead = 'read'
        this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextMarkRead')
      } else if (type === 'unread') {
        this.actionRead = 'unread'
        this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextMarkUnRead')
      } else if (type === 'archive') {
        this.actionRead = 'archive'
        this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextArchive')
      } else if (type === 'delete') {
        this.actionRead = 'delete'
        this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextDelete')
      } else if (type === 'restore') {
        this.actionRead = 'restore'
        if (this.filterFolder === 'FILTER_FOLDER_ARCHIVED') {
          this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextRestoreFromArchive')
        } else {
          this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextRestore')
        }
      } else if (type === 'emptyTrash') {
        this.actionRead = 'emptyTrash'
        this.textConfirmation = this.$t('message.marketingMessages.confirmModal.confirmationTextEmptyTrash')
      }
      this.$refs.confirmationModal?.show()
    },
    onBulkAction(type) {
      if (this.selectedEntriesIds.length === 0) {
        return
      }
      this.changeStatus(type)
    },
    onBulkDropdownShown() {
      if (this.bulkDropdownHiddenTimeout) {
        clearTimeout(this.bulkDropdownHiddenTimeout)
        this.bulkDropdownHiddenTimeout = null
      }
      this.suppressBulkCheckboxClicks = false
    },
    onBulkDropdownHidden() {
      // Swallow leftover clicks that land on nearby checkboxes when the menu closes (COOZ-16042)
      this.suppressBulkCheckboxClicks = true
      if (this.bulkDropdownHiddenTimeout) {
        clearTimeout(this.bulkDropdownHiddenTimeout)
      }
      this.bulkDropdownHiddenTimeout = setTimeout(() => {
        this.suppressBulkCheckboxClicks = false
        this.bulkDropdownHiddenTimeout = null
      }, 300)
    },
    async refreshUnreadBadge() {
      const header = this.$parent?.$refs?.['coozzy-header']
      if (!header) {
        return
      }
      try {
        if (header.mailboxesForCurrentUser?.length > 0) {
          const mailboxIds = header.mailboxesForCurrentUser.map(mailbox => mailbox.id)
          header.numberMessage = await this.loadEmailCounts({
            mailboxIds,
            force: true
          })
        } else {
          header.countUnreadMails?.(true)
        }
      } catch (e) {
        // Count refresh is best-effort. A rejection must not surface as a failed archive, delete, or read action.
        console.error('Error refreshing unread badge:', e)
      }
    },
    isToday(timestamp) {
      if (!timestamp || timestamp === -1 || timestamp === '-1') {
        return false
      }
      const date = new Date(timestamp * 1000)
      const today = new Date()
      return date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
    },
    formatTime(timestamp) {
      const date = new Date(timestamp * 1000)
      // Format hours and minutes with leading zeros if needed
      return (date.getHours() < 10 ? '0' : '') + date.getHours() + ':' + (date.getMinutes() < 10 ? '0' : '') + date.getMinutes()
    },
    formatTimestampForList(timestamp) {
      if (!timestamp || timestamp === -1 || timestamp === '-1') {
        return '-'
      }
      if (this.isToday(timestamp)) {
        return this.formatTime(timestamp)
      }
      return this.$options.filters.timestampToDateTime(timestamp)
    },
    getMailTimestamp(mail) {
      const timestamp = Number(mail?.timestamp)
      return Number.isFinite(timestamp) ? timestamp : 0
    },
    dedupeMailsById(mails = []) {
      const seenIds = new Set()
      return mails.filter(mail => {
        if (seenIds.has(mail.id)) {
          return false
        }
        seenIds.add(mail.id)
        return true
      })
    },
    mergeIncomingMails(newMails = []) {
      const mergedMails = [...this.messageList]
      newMails.forEach(mail => {
        if (!mergedMails.find(item => item.id === mail.id)) {
          mergedMails.push(mail)
        }
      })
      this.messageList = mergedMails.sort((a, b) => this.getMailTimestamp(b) - this.getMailTimestamp(a))
    },
    async refreshColumnViewAfterSend() {
      if (!this.columnView || this.allMailboxesForCurrentUser.length === 0) {
        return
      }
      this.captureListRestoreState()
      this.isBusy = true
      this.disabledCheckbox = false
      const unreadFilter = this.filterUnreadMsg === true ? 'APPLIES' : 'UNDEFINED_VALUE'
      const mailboxIds = this.filterMailboxes.length > 0
        ? this.filterMailboxes.map(obj => obj.id)
        : this.allMailboxesForCurrentUser.map(mailbox => mailbox.id)
      const previouslyLoadedPages = Math.max(1, this.maxLoadedPageInColumnView)
      let effectiveLastPage = previouslyLoadedPages
      const mergedById = new Map()
      const mergedMails = []

      try {
        for (let page = 1; page <= effectiveLastPage; page++) {
          const response = await MessageApi.getListMails(
            this.filterFolder,
            [],
            unreadFilter,
            this.nbrPerPage,
            mailboxIds,
            page,
            this.querySearch,
            'DESC',
            false
          )
          if (!response) {
            break
          }
          this.messageCount = response.totalCount
          const totalPages = Math.max(1, Math.ceil(response.totalCount / this.nbrPerPage))
          effectiveLastPage = Math.min(previouslyLoadedPages, totalPages)
          response.mails.forEach(mail => {
            if (!mergedById.has(mail.id)) {
              mergedById.set(mail.id, true)
              mergedMails.push(mail)
            }
          })
        }

        this.messageList = mergedMails.sort((a, b) => this.getMailTimestamp(b) - this.getMailTimestamp(a))
        this.maxLoadedPageInColumnView = effectiveLastPage
        this.currentPage = effectiveLastPage
        this.applyPendingSelectionImmediate()
        this.loadContacts()
        this.getReminders()
        this.getTickets()
        this.setMailsLoadedAt(Date.now())
        this.disabledDeleteBtn = false
        this.mailBoxFilterChanged = false
        this.isBusy = false
        this.refreshDone(false)
        this.restoreScrollAfterRefresh()
      } catch (error) {
        console.error(error)
        this.isBusy = false
        this.refreshDone(false)
        this.disabledDeleteBtn = false
        Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
      }
    },
    async filterMessages(page = 1, newEmailsComing = false, { skipAbort = false } = {}) {
      if (this.filterMailboxes.length > 0 || this.allMailboxesForCurrentUser.length > 0) {
        const isPrimaryRefresh = ((page === 1 && this.columnView) || !this.columnView) && !newEmailsComing

        if ((page === 1 && this.columnView) || !this.columnView) {
          if (!newEmailsComing) {
            this.selectedEntries = []
            this.selectAllVisibleEntries = false
            this.selectAllEntries = false
            this.isBusy = true
          }
        }
        this.disabledCheckbox = false
        const unreadFilter = this.filterUnreadMsg === true ? 'APPLIES' : 'UNDEFINED_VALUE'

        if (!skipAbort) {
          // Cancel the previous request, if there is one
          if (this.abortController) {
            this.abortController.abort()
          }
        }

        // Create a new AbortController
        this.abortController = new AbortController()

        try {
          if (((page === 1 && this.columnView) || !this.columnView) && !newEmailsComing) {
            this.messageCount = 0
            this.messageList = []
          }
          const requestPage = newEmailsComing ? 1 : page
          const response = await MessageApi.getListMails(
            this.filterFolder,
            [],
            unreadFilter,
            this.nbrPerPage,
            this.filterMailboxes.length > 0
              ? this.filterMailboxes.map(obj => obj.id)
              : this.allMailboxesForCurrentUser.map(mailbox => mailbox.id),
            requestPage,
            this.querySearch,
            'DESC',
            false,
            { signal: this.abortController.signal }
            // Pass the cancel token metadata to the request
          )

          // If the request was cancelled, don't update the messages
          if (this.abortController.signal.aborted) {
            return
          }
          if (response) {
            this.messageCount = response.totalCount
            if (((page === 1 && this.columnView) || !this.columnView) && !newEmailsComing) {
              this.messageList = this.dedupeMailsById(response.mails || [])
            } else if (newEmailsComing) {
              this.mergeIncomingMails(response?.mails || [])
            } else {
              // Prevent duplicate mails in pagination (unstable order → duplicate ids)
              const existingIds = new Set(this.messageList.map(item => item.id))
              response?.mails?.forEach(mail => {
                if (!existingIds.has(mail.id)) {
                  existingIds.add(mail.id)
                  this.messageList.push(mail)
                }
              })
            }
            if (!newEmailsComing) {
              if (this.columnView) {
                this.maxLoadedPageInColumnView = page === 1 ? 1 : Math.max(this.maxLoadedPageInColumnView, page)
              } else {
                this.maxLoadedPageInColumnView = 1
              }
            }
            // Restore the previously selected mail synchronously so the preview pane
            // does not unmount between now and the asynchronous scroll restore below.
            this.applyPendingSelectionImmediate()
            this.loadContacts()
            this.getReminders()
            this.getTickets()
            this.setMailsLoadedAt(Date.now())
            this.disabledDeleteBtn = false
            this.mailBoxFilterChanged = false
            if (isPrimaryRefresh) {
              this.isBusy = false
              this.refreshDone(newEmailsComing)
            }
            this.restoreScrollAfterRefresh()
            this.$nextTick(() => {
              if (!newEmailsComing) {
                this.currentPage = page
              }
            })
          }
        } catch (error) {
          console.log(error)
          if (error.name === 'AbortError') {
            console.log('Request cancelled:', error.message)
            if (isPrimaryRefresh) {
              this.isBusy = true
            }
          } else {
            console.error(error)
            if (isPrimaryRefresh) {
              this.isBusy = false
            }
            this.refreshDone(newEmailsComing)
            this.disabledDeleteBtn = false
          }
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        } finally {
          if (!newEmailsComing && this.inFlightLoadMorePage === page) {
            this.inFlightLoadMorePage = null
          }
        }
      }
    },
    async filterAllMessages() {
      const unreadFilter = this.filterUnreadMsg === true ? 'APPLIES' : 'UNDEFINED_VALUE'

      try {
        const response = await MessageApi.listMailIds(
          this.filterFolder,
          [],
          unreadFilter,
          this.filterMailboxes.length > 0
            ? this.filterMailboxes.map(obj => obj.id)
            : this.allMailboxesForCurrentUser.map(mailbox => mailbox.id),
          this.querySearch,
          'UNDEFINED_SORT_ORDER'
        )

        if (response) {
          return response.ids
        }
      } catch (error) {
        console.log(error)
      }
    },
    loadContacts() {
      const uniqueContactIds = [...new Set(
        this.messageList.flatMap(mail => mail.referenceIds).filter(id => id?.startsWith('contact_'))
      )]

      if (uniqueContactIds.length === 0) {
        this.isBusy = false
        return
      }

      ContactApi.contactResolve(uniqueContactIds)
        .then(response => {
          this.usedContacts = response.persons.concat(response.companies)
          this.setContacts(this.usedContacts)
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    async archiveMails() {
      const messagesIdsToArchive = [...this.selectedEntriesIds]

      if (!messagesIdsToArchive.length) {
        console.warn('No messages selected for archiving')
        return
      }

      this.disabledDeleteBtn = true
      this.setChangingStatusMails(messagesIdsToArchive)
      this.$refs.confirmationModal.hide()
      this.disabledCheckbox = true

      this.messageList = this.messageList.filter(item => !messagesIdsToArchive.includes(item.id))

      try {
        await executeBulkInChunks(messagesIdsToArchive, 400, (chunk) => MessageApi.archiveMails(chunk))

        for (const mailId of messagesIdsToArchive) {
          this.addReadMail({
            mailId: mailId,
            accountId: this.accountId
          })
        }

        if (this.abortController) {
          this.abortController.abort()
          this.abortController = null
        }
        await this.refreshList(null, { skipAbort: true })
        await this.refreshUnreadBadge()
      } catch (e) {
        console.error('Error in archiveMails:', e)
        Vue.toasted.show(this.$t('message.archiveErrors.mail'), { type: 'error' })
      } finally {
        this.disabledCheckbox = false
        this.disabledDeleteBtn = false
      }
    },
    async deleteMails() {
      const messagesIdsToDelete = [...this.selectedEntriesIds]

      if (!messagesIdsToDelete.length) {
        console.warn('No messages selected for deletion')
        return
      }

      this.disabledDeleteBtn = true
      this.setChangingStatusMails(messagesIdsToDelete)
      this.$refs.confirmationModal.hide()
      this.disabledCheckbox = true

      this.mailIdDeletedForClosingModal = this.mailIdDeletedForClosingModal.concat(messagesIdsToDelete)
      this.appRefreshAction = 'delete'
      this.messageList = this.messageList.filter(item => !messagesIdsToDelete.includes(item.id))

      try {
        if (this.filterFolder === 'FILTER_FOLDER_ARCHIVED') {
          await executeBulkInChunks(messagesIdsToDelete, 400, (chunk) => MessageApi.unarchiveMails(chunk))
        }

        await executeBulkInChunks(messagesIdsToDelete, 400, (chunk) => MessageApi.deleteMails(chunk))
        await executeBulkInChunks(messagesIdsToDelete, 400, (chunk) => MessageApi.mailMarkRead(chunk, this.accountId))

        if (this.abortController) {
          this.abortController.abort()
          this.abortController = null
        }
        await this.refreshList(null, { skipAbort: true })
        await this.refreshUnreadBadge()
      } catch (e) {
        console.error('Error in deleteMails:', e)
        Vue.toasted.show(this.$t('message.deleteErrors.mail'), { type: 'error' })
      } finally {
        this.disabledCheckbox = false
        this.disabledDeleteBtn = false
      }
    },
    async restoreMails() {
      const idsToRestore = [...this.selectedEntriesIds]
      this.disabledDeleteBtn = true
      this.setChangingStatusMails(idsToRestore)
      this.$refs.confirmationModal.hide()
      this.disabledCheckbox = true
      this.appRefreshAction = 'restore'
      this.messageList = this.messageList.filter(item => !idsToRestore.includes(item.id))

      try {
        if (this.filterFolder === 'FILTER_FOLDER_ARCHIVED') {
          await executeBulkInChunks(idsToRestore, 400, (chunk) => MessageApi.unarchiveMails(chunk))
        } else {
          await executeBulkInChunks(idsToRestore, 400, (chunk) => MessageApi.restoreMails(chunk))
        }

        if (this.abortController) {
          this.abortController.abort()
          this.abortController = null
        }
        await this.refreshList(null, { skipAbort: true })
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.deleteErrors.mail'), { type: 'error' })
      } finally {
        this.disabledCheckbox = false
        this.disabledDeleteBtn = false
      }
    },
    async emptyTrash() {
      const idsToEmpty = [...this.selectedEntriesIds]
      this.setChangingStatusMails(idsToEmpty)
      this.$refs.confirmationModal.hide()
      this.disabledCheckbox = true
      this.disabledDeleteBtn = true
      this.appRefreshAction = 'emptyTrash'
      this.messageList = this.messageList.filter(item => !idsToEmpty.includes(item.id))

      try {
        await executeBulkInChunks(idsToEmpty, 400, (chunk) => MessageApi.emptyTrash(chunk))

        if (this.abortController) {
          this.abortController.abort()
          this.abortController = null
        }
        await this.refreshList(null, { skipAbort: true })
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.deleteErrors.mail'), { type: 'error' })
      } finally {
        this.disabledCheckbox = false
        this.disabledDeleteBtn = false
      }
    },
    async mailDeleted(id) {
      this.appRefreshAction = 'delete'
      this.disabledCheckbox = true
      this.mailIdDeletedForClosingModal.push(id)
      // remove the deleted items from the messageList
      this.messageList = this.messageList.filter(item => item.id !== id)
      if (!this.columnView) {
        await this.refreshList()
        return
      }
      this.refreshDone()
    },
    async mailArchived(id) {
      // this.appRefreshAction = 'archive'
      this.disabledCheckbox = true
      // remove the deleted items from the messageList
      this.messageList = this.messageList.filter(item => item.id !== id)
      if (!this.columnView) {
        await this.refreshList()
        return
      }
      this.refreshDone()
    },
    async mailRestored(id) {
      this.appRefreshAction = 'restore'
      this.disabledCheckbox = true
      // remove the deleted items from the messageList
      this.messageList = this.messageList.filter(item => item.id !== id)
      if (!this.columnView) {
        await this.refreshList()
        return
      }
      this.refreshDone()
    },
    async refreshList(mailIdToRemove = null, { skipAbort = false } = {}) {
      if (mailIdToRemove) {
        this.messageList = this.messageList.filter(item => item.id !== mailIdToRemove)
      }
      let page = this.currentPage
      if (this.messageList.length === 0) {
        page = this.currentPage > 1 ? this.currentPage - 1 : this.currentPage
      }
      this.filterMessages(page, false, { skipAbort })
    },
    handleCheckboxesInsideTable(value) {
      if (this.suppressBulkCheckboxClicks) {
        return
      }
      if (value) {
        this.selectedEntries = this.messageList.map(m => m.id)
      } else {
        this.selectedEntries = []
      }
      this.selectAllVisibleEntries = value
      if (!value) {
        this.selectAllEntries = false
      }
    },
    itemSelected(id, checked) {
      if (this.suppressBulkCheckboxClicks) {
        return
      }
      if (checked && !this.selectedEntries.includes(id)) {
        this.selectedEntries.push(id)
        const visibleIds = this.messageList.map(m => m.id)
        if (visibleIds.length && visibleIds.every(vid => this.selectedEntries.includes(vid))) {
          this.selectAllVisibleEntries = true
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter(entry => entry !== id)
        this.selectAllEntries = false
        this.selectAllVisibleEntries = false
      }
    },
    onRowClicked(item, index, event) {
      const target = event?.target || event?.srcElement
      const isCheckboxClick = Boolean(target?.closest?.('.checkbox-cell') ||
        target?.closest?.('.custom-checkbox') ||
        target?.closest?.('.custom-control-label') ||
        target?.closest?.('.custom-control-input'))
      if (isCheckboxClick) {
        this.selectedEmailId = item.id
        this.$refs.messagesTable.clearSelected()
        this.dynamicId = Math.random().toString(36).substr(2, 9)
        return
      }
      this.selectedEmailId = item.id
      this.$refs.messagesTable.clearSelected()
      this.dynamicId = Math.random().toString(36).substr(2, 9)
      if (event.target && event.target.parentElement && event.target.parentElement._prevClass && event.target.parentElement._prevClass.includes('reminderIcon')) {
        this.selectedMail = item
        return false
      } else {
        this.showDetail(item)
      }
    },
    onRowDblClicked(item) {
      this.dynamicId = Math.random().toString(36).substr(2, 9)
      this.selectedMail = item
      this.setMailDetailMailObject(item)
      this.setMailDetailTicketObject(this.isMailHasReferenceIdTicket(item))
      this.$nextTick(function () {
        this.callShowDetailMailModal()
      })
    },
    onRowMiddleClicked(item, index, event) {
      this.dynamicId = Math.random().toString(36).substr(2, 9)
      if (event.srcElement.classList.contains('icon-link-ticket')) {
        this.openMiddleDetail(this.isMailHasReferenceIdTicket(item))
      }
    },
    onModalHidden() {
      this.clearCheckbox()
    },
    onRowHovered(item) {
      this.popoverMessage = item
    },
    onRowUnHovered() {
      this.popoverMessage = null
      this.hidePopover()
    },
    onMouseMove(e) {
      if (this.columnView) {
        this.hidePopover()
        return
      }
      this.ensurePopoverRefs()
      const target = e?.srcElement || e?.target
      const shouldShow = target ? this.hasSomeParentTheClass(target, 'fieldToShow') : false

      if (!shouldShow || !this.popoverMessage || !this.popoverEl) {
        this.hidePopover()
        return
      }

      this.popoverLastEvent = e
      if (this.popoverRafId) return

      this.popoverRafId = requestAnimationFrame(() => {
        this.popoverRafId = null
        this.showPopoverAt(this.popoverLastEvent)
      })
    },
    ensurePopoverRefs() {
      if (!this.popoverState) {
        this.popoverState = {
          visible: false,
          lastMessageId: null,
          lastMessage: null
        }
      }
      if (!this.popoverEl) {
        this.popoverEl = this.$refs.eventPopover
        this.popoverSubjectEl = this.$refs.popoverSubject
        this.popoverShadow = this.$refs.popoverBodyShadow
      }
    },
    showPopoverAt(e) {
      if (!this.popoverEl || !this.popoverMessage) return

      if (!this.popoverState.visible) {
        this.popoverEl.style.display = 'block'
        this.popoverState.visible = true
      }

      if (this.popoverState.lastMessage !== this.popoverMessage) {
        this.popoverState.lastMessage = this.popoverMessage
        this.popoverState.lastMessageId = this.popoverMessage.id
        if (this.popoverSubjectEl) {
          this.popoverSubjectEl.textContent = this.decodeHtmlEntitiesIfNeeded(this.popoverMessage.subject || '')
        }
        if (this.popoverShadow?.setHtml) {
          let html = this.popoverMessage.bodyHtml || ''
          if (!html && this.popoverMessage.bodyText) {
            html = this.escapeHtml(this.decodeHtmlEntitiesIfNeeded(String(this.popoverMessage.bodyText))).replaceAll('\n', '<br>')
          }
          html = html.replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*\/?>/gi, '')
          this.popoverShadow.setHtml(html)
        }
        this.popoverSize = {
          width: this.popoverEl.clientWidth || 458,
          height: this.popoverEl.clientHeight || 200
        }
      }

      this.positionPopover(e)
    },
    positionPopover(e) {
      if (!this.popoverEl || !e) return

      const tooltipWidth = this.popoverSize?.width || this.popoverEl.clientWidth || 458
      const tooltipHeight = this.popoverSize?.height || this.popoverEl.clientHeight || 200
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const scrollY = window.scrollY || window.pageYOffset
      const padding = 20

      const spaceRight = windowWidth - e.pageX - padding
      const spaceLeft = e.pageX - padding
      const spaceBottom = windowHeight - (e.pageY - scrollY) - padding
      const spaceTop = (e.pageY - scrollY) - padding

      let left
      let top

      if (spaceRight >= tooltipWidth) {
        left = e.pageX + 10
      } else if (spaceLeft >= tooltipWidth) {
        left = e.pageX - tooltipWidth - 10
      } else {
        left = Math.max(padding, Math.min(windowWidth - tooltipWidth - padding, e.pageX - tooltipWidth / 2))
      }

      if (spaceBottom >= tooltipHeight) {
        top = e.pageY + 10
      } else if (spaceTop >= tooltipHeight) {
        top = e.pageY - tooltipHeight - 10
      } else if (spaceBottom > spaceTop) {
        top = scrollY + windowHeight - tooltipHeight - padding
      } else {
        top = scrollY + padding
      }

      left = Math.max(padding, Math.min(left, windowWidth - tooltipWidth - padding))
      top = Math.max(scrollY + padding, Math.min(top, scrollY + windowHeight - tooltipHeight - padding))

      this.popoverEl.style.left = left + 'px'
      this.popoverEl.style.top = top + 'px'
    },
    hidePopover() {
      if (this.popoverRafId) {
        cancelAnimationFrame(this.popoverRafId)
        this.popoverRafId = null
      }
      if (!this.popoverEl || !this.popoverState?.visible) return
      this.popoverEl.style.display = 'none'
      this.popoverState.visible = false
    },
    paginateTo(page) {
      this.filterMessages(page)
    },
    loadMore() {
      if (this.isBusy) return

      const totalPages = this.messageCount > 0
        ? Math.ceil(this.messageCount / this.nbrPerPage)
        : 1
      const nextPage = this.currentPage + 1

      if (nextPage > totalPages || this.inFlightLoadMorePage === nextPage) {
        return
      }

      this.inFlightLoadMorePage = nextPage
      this.filterMessages(nextPage)
    },
    replyMail(email) {
      this.prepareReplyMail(email)
    },
    replyAllMail(email) {
      this.prepareReplyAll(email)
    },
    forwardMail(email) {
      this.prepareForwardMail(email)
    },
    printMessage(shortContent) {
      this.shortContent = shortContent
    },
    async setPublish() {
      const publishAction = this.actionRead
      if (this.disabledDeleteBtn || (publishAction !== 'read' && publishAction !== 'unread')) {
        return
      }

      const messageIds = [...this.selectedEntriesIds]
      if (!messageIds.length) {
        return
      }

      this.disabledDeleteBtn = true
      this.disabledCheckbox = true
      this.$refs.confirmationModal.hide()

      try {
        if (publishAction === 'read') {
          await executeBulkInChunks(messageIds, 400, (chunk) => MessageApi.mailMarkRead(chunk, this.accountId))
          await this.refreshUnreadBadge()
          for (const mailId of messageIds) {
            this.addReadMail({
              mailId: mailId,
              accountId: this.accountId
            })
          }
          this.handleCheckboxesInsideTable(false)
          Vue.toasted.show(this.$t('message.successMessages.mailRead'), { type: 'success' })
        } else {
          await executeBulkInChunks(messageIds, 400, (chunk) => MessageApi.mailMarkUnread(chunk, this.accountId))
          await this.refreshUnreadBadge()
          for (const mailId of messageIds) {
            this.addUnreadMail({
              mailId: mailId,
              accountId: this.accountId
            })
          }
          this.handleCheckboxesInsideTable(false)
          Vue.toasted.show(this.$t('message.successMessages.mailUnread'), { type: 'success' })
        }
      } catch (e) {
        console.log(e)
        const errorKey = publishAction === 'read'
          ? 'message.savingErrors.mailRead'
          : 'message.savingErrors.mailUnread'
        Vue.toasted.show(this.$t(errorKey), { type: 'error' })
      } finally {
        this.disabledCheckbox = false
        this.disabledDeleteBtn = false
      }
    },
    showDetail(mail) {
      this.selectedMail = mail
      if (!this.columnView || this.mobileView) {
        this.setMailDetailMailObject(mail)
        this.setMailDetailTicketObject(this.isMailHasReferenceIdTicket(mail))
        this.$nextTick(function () {
          this.callShowDetailMailModal()
        })
      }
    },
    clickOnClear() {
      this.querySearch = ''
      this.filterFolder = 'FILTER_FOLDER_ALL'
      this.filterMailboxes = []
      this.filterUnreadMsg = false
      this.$nextTick(function () {
        this.checkfilter = false
      })
    },
    refreshDone(newEmailsComing = false) {
      if (this.appRefreshAction === 'delete') {
        if (this.$refs['mail-detail-modal'] && this.mailIdDeletedForClosingModal.includes(this.$refs['mail-detail-modal'].email?.id)) {
          this.mailIdDeletedForClosingModal = []
          this.$nextTick(function () {
            // this.$refs['mail-detail-modal'].loading = false
            this.$bvModal?.hide('modal-detail-mail')
          })
        }
      } else if (this.appRefreshAction === 'restore') {
        if (this.filterFolder === 'FILTER_FOLDER_ARCHIVED') {
          Vue.toasted.show(this.$t('message.successMessages.restoreMailFromArchive'), { type: 'success' })
        } else {
          Vue.toasted.show(this.$t('message.successMessages.restoreMail'), { type: 'success' })
        }
      } else if (this.appRefreshAction === 'emptyTrash') {
        Vue.toasted.show(this.$t('message.successMessages.emptyTrashMail'), { type: 'success' })
      }

      this.disabledCheckbox = false
      this.disabledDeleteBtn = false

      if (this.$refs.confirmationModal) {
        this.$refs.confirmationModal.hide()
      }
      if (!newEmailsComing) {
        // While restoring list state after a reply/forward send (COOZ-15299), skip the
        // selection wipe so the preview pane doesn't unmount and the row stays highlighted.
        if (!this.pendingListRestore) {
          this.clearCheckbox()
        }
        this.resetStatusMails()
      }

      this.appRefreshAction = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.messages-table .custom-control-input {
  top: 4px;
  height: 1rem;
}
:deep(.custom-checkbox .btn-secondary) {
  height: auto;
  min-height: 31px;
  max-height: 50px;
}
:deep(.message-text) {
  width: 330px;
}
:deep(.adjustWidth) {
  max-width: 90px;
  min-width: 85px;
  padding: 0 !important;
    > div {
    max-width: 90px;
    }
}
p.text-msg {
  line-height: 1.5em;
  height: 3em;
  overflow: hidden;
  line-break: anywhere;
}
.detail-message {
  height: calc(70vh + 32.19px);
  overflow: auto;
  -webkit-box-shadow: 1px 1px 6px #ccc;
  box-shadow: 1px 1px 6px #ccc !important;
  border-bottom-left-radius: 0rem;
  border-top-left-radius: 0rem;
  border: 0;
}
.parent-container {
  display: flex;
  flex-direction: row;
}
.simple-container {
  :deep(.gutter-horizontal) {
    display: none;
  }
}
:deep(.gutter-horizontal) {
  width: 2px !important;
  border: 1px solid grey;
  height: calc(70vh + 33.19px);
  margin-top: 7px;
  cursor: e-resize;
}
.leftside,
.rightside {
  flex: 1 1 auto;
  overflow: auto;
}

.floating-wrapper {
  right: 5vw;
  bottom: 4vh;
  z-index: 9;
  position: fixed;

  .floating-button {
    border-radius: 100px !important;
    display: flex;
    align-items: center;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.20), 0 4px 4px rgba(0, 0, 0, 0.15);
    z-index: 2;
    justify-content: center;
    background-color: $color-bg-green;
    padding: 20px !important;
    cursor: pointer;

    svg {
      color: white;
      font-size: 15px;
    }
  }
}
:deep(.table-column-view table.table) {
  width: 100%;
  border-bottom-right-radius: 0rem;
  border-top-right-radius: 0rem;
  .width-full-content {
    width: stretch;
  }
  p.text-msg {
    height: 1.5em;
    font-size: 0.8rem;
  }
  tbody {
    height: 70vh;
    overflow-y: scroll;
    display: block;
  }
  tbody tr {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  tbody tr.b-table-row-selected {
    position: relative;
  }
  tbody tr.b-table-row-selected::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: $color-primary;
  }

  thead {
    width: calc(100% + 17px); /* Adjust for scrollbar */
    display: table;
  }

  &.b-table-selecting > tbody > .b-table-row-selected td {
    background-color: white;
    background-image: -webkit-gradient(linear, left top, left bottom, from(rgba(0, 0, 0, 0.075)), to(rgba(0, 0, 0, 0.075)));
    background-image: linear-gradient(rgba(0, 0, 0, 0.075), rgba(0, 0, 0, 0.075));
    background-repeat: no-repeat;
    color: inherit;
  }
}

.other :deep(.archivedMail) {
  display: none;
}

.other :deep(.trashedMail) {
  display: none;
}

.archive :deep(.trashedMail) {
  display: none;
}

.archive :deep(.otherMail) {
  display: none;
}

.trash :deep(.archivedMail) {
  display: none;
}

.trash :deep(.otherMail) {
  display: none;
}

:deep(.deletedMail) {
  display: none;
}
//:deep(.checkbox-td) {
  //max-width: 20px;
//}
.block-center {
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  text-align: center;
  .text-icon{
    font-size: 20px;
  }
  .icon {
    width: 70px;
    height: auto;
  }
}
.eventPopover {
  position: absolute;
  z-index: 9999;
  pointer-events: none;
  width: 458px !important;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-box-shadow: 1px 1px 6px #ccc;
  box-shadow: 1px 1px 6px #ccc;
  :deep(.employees-calendar) {
    width: 100%;
    border-radius: 5px;
    background: #ededed;
    color: black;
    font-size: 14px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-height: calc(100vh - 60px);
    overflow-y: auto;
    position: relative;
    margin-top: 0 !important;
    -webkit-box-shadow: 1px 1px 6px #ccc !important;
    box-shadow: 1px 1px 6px #ccc !important;
    h4 {
      font-size: 20px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
    }
    p {
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      white-space: normal;
      word-break: break-word;
    }
    :deep(*) {
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      word-break: break-word;
      max-width: 100%;
    }
  }
}
@media (max-width: 480px) {
  :deep(.unread-button label) {
    padding-left: 0;
    padding-right: 0;
    font-size: 12px !important;
  }
}

@media (max-width: 991px) {
 .table-column-view {
    height: 50vh;
    overflow-y: scroll;
  }
}
</style>
